import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2025 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { stringToHtml, isUndefined, normalizeWriteInText, translate, } from "@sequentech/ui-core";
import { Candidate } from "@sequentech/ui-essentials";
import Image from "mui-image";
import { resetBallotSelection, selectBallotSelectionQuestion, selectBallotSelectionVoteChoice, setBallotSelectionBlankVote, setBallotSelectionInvalidVote, setBallotSelectionVoteChoice, } from "../../store/ballotSelections/ballotSelectionsSlice";
import { checkAllowWriteIns, checkIsInvalidVote, checkIsWriteIn, getImageUrl, getLinkUrl, } from "../../services/ElectionConfigService";
import { useTranslation } from "react-i18next";
import { SettingsContext } from "../../providers/SettingsContextProvider";
import { provideBallotService } from "../../services/BallotService";
export var Answer = function (_a) {
    var answer = _a.answer, contestId = _a.contestId, ballotStyle = _a.ballotStyle, hasCategory = _a.hasCategory, isSelectable = _a.isSelectable, iconCheckboxPolicy = _a.iconCheckboxPolicy, isReview = _a.isReview, isInvalidVoteInput = _a.isInvalidVote, isExplicitBlankVote = _a.isExplicitBlankVote, isInvalidWriteIns = _a.isInvalidWriteIns, isRadioSelection = _a.isRadioSelection, contest = _a.contest, selectedChoicesSum = _a.selectedChoicesSum, setSelectedChoicesSum = _a.setSelectedChoicesSum, disableSelect = _a.disableSelect, explicitBlank = _a.explicitBlank, setExplicitBlank = _a.setExplicitBlank, setIsTouched = _a.setIsTouched;
    var isPreferential = provideBallotService().isPreferential;
    var isPreferentialVote = useMemo(function () {
        if (!contest.counting_algorithm)
            return false;
        return isPreferential(contest.counting_algorithm);
    }, [contest.counting_algorithm]);
    var totalCandidates = contest.candidates.length;
    var selectionState = useAppSelector(selectBallotSelectionVoteChoice(ballotStyle.election_id, contestId, answer.id));
    var questionState = useAppSelector(selectBallotSelectionQuestion(ballotStyle.election_id, contestId));
    var question = ballotStyle.ballot_eml.contests.find(function (contest) { return contest.id === contestId; });
    var dispatch = useAppDispatch();
    var globalSettings = useContext(SettingsContext).globalSettings;
    var imageUrl = getImageUrl(answer);
    var infoUrl = getLinkUrl(answer);
    var i18n = useTranslation().i18n;
    var ballotService = provideBallotService();
    var isInvalidVote = useMemo(function () { return isInvalidVoteInput !== null && isInvalidVoteInput !== void 0 ? isInvalidVoteInput : checkIsInvalidVote(answer); }, [isInvalidVoteInput, answer]);
    var _b = useState(null), selectedPosition = _b[0], setSelectedPosition = _b[1];
    useEffect(function () {
        var _a;
        var sel = (_a = selectionState === null || selectionState === void 0 ? void 0 : selectionState.selected) !== null && _a !== void 0 ? _a : -1;
        setSelectedPosition(sel + 1); // Selected positions in the UI start at 1, not 0. And 0 means no selection
    }, [selectionState]);
    var isChecked = function () {
        if (isInvalidVote) {
            return !isUndefined(questionState) && questionState.is_explicit_invalid;
        }
        else if (isExplicitBlankVote) {
            return (!isUndefined(questionState) &&
                !!ballotService.checkIsBlank(questionState) &&
                explicitBlank);
        }
        else {
            return !isUndefined(selectionState) && selectionState.selected > -1;
        }
    };
    var setInvalidVote = function (value) {
        dispatch(setBallotSelectionInvalidVote({
            ballotStyle: ballotStyle,
            contestId: contestId,
            isExplicitInvalid: value,
        }));
    };
    var setBlankVote = function () {
        setExplicitBlank(true);
        dispatch(setBallotSelectionBlankVote({
            ballotStyle: ballotStyle,
            contestId: contestId,
        }));
    };
    var handlePreferentialChange = function (position) {
        if (!isSelectable || isReview) {
            return;
        }
        setIsTouched(true);
        setSelectedPosition(position);
        var cleanedText = (selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text) && normalizeWriteInText(selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text);
        dispatch(setBallotSelectionVoteChoice({
            ballotStyle: ballotStyle,
            contestId: contestId,
            voteChoice: {
                id: answer.id,
                selected: position ? position - 1 : -1,
                write_in_text: cleanedText,
            },
        }));
    };
    var setChecked = function (value) {
        if (!isSelectable || isReview || isPreferentialVote) {
            return;
        }
        setIsTouched(true);
        if (isInvalidVote) {
            setInvalidVote(value);
            return;
        }
        if (isExplicitBlankVote) {
            if (value) {
                setBlankVote();
            }
            else {
                setExplicitBlank(false);
            }
            return;
        }
        else if (value && explicitBlank) {
            setExplicitBlank(false);
        }
        var cleanedText = (selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text) && normalizeWriteInText(selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text);
        if (isRadioSelection) {
            dispatch(resetBallotSelection({
                ballotStyle: ballotStyle,
                force: true,
                contestId: contest.id,
            }));
        }
        dispatch(setBallotSelectionVoteChoice({
            ballotStyle: ballotStyle,
            contestId: contestId,
            voteChoice: {
                id: answer.id,
                selected: value ? 0 : -1,
                write_in_text: cleanedText,
            },
        }));
    };
    var shouldDisable = disableSelect && (selectionState === null || selectionState === void 0 ? void 0 : selectionState.selected) === -1;
    var isWriteIn = checkIsWriteIn(answer);
    var allowWriteIns = question && checkAllowWriteIns(question);
    var setWriteInText = function (writeInText) {
        if (!isWriteIn || !allowWriteIns || !isSelectable || isReview) {
            return;
        }
        var cleanedText = normalizeWriteInText(writeInText);
        dispatch(setBallotSelectionVoteChoice({
            ballotStyle: ballotStyle,
            contestId: contestId,
            voteChoice: {
                id: answer.id,
                selected: isUndefined(selectionState) ? -1 : selectionState.selected,
                write_in_text: cleanedText,
            },
        }));
    };
    if (isReview && !isChecked()) {
        return null;
    }
    if (isReview && !!isExplicitBlankVote) {
        return null;
    }
    return (_jsx(Candidate, { isPreferentialVote: isPreferentialVote, totalCandidates: totalCandidates, maxVotes: contest.max_votes, title: translate(answer, "name", i18n.language), description: stringToHtml(translate(answer, "description", i18n.language) || ""), isSelectable: isSelectable, checked: isChecked(), setChecked: setChecked, url: infoUrl, hasCategory: hasCategory, isWriteIn: allowWriteIns && isWriteIn, writeInValue: selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text, setWriteInText: setWriteInText, isInvalidVote: isInvalidVote, isInvalidWriteIn: !!(selectionState === null || selectionState === void 0 ? void 0 : selectionState.write_in_text) && isInvalidWriteIns, shouldDisable: shouldDisable, iconCheckboxPolicy: iconCheckboxPolicy, selectedPosition: selectedPosition, handlePreferentialChange: handlePreferentialChange, children: imageUrl ? (_jsx(Image, { src: "".concat(globalSettings.PUBLIC_BUCKET_URL).concat(imageUrl), duration: 100 })) : null }));
};
